import Image from 'next/image';
import { RichText } from 'prismic-reactjs';
import { useEffect, useRef, useState } from 'react';
import { fluidProp } from 'remote-com-shared/style/helpers';
import styled, { css } from 'styled-components';

import {
  ProgressBarSectionWrapper,
  ProgressBarSectionHeader,
  ProgressBarContainer,
  ActiveNumberLineSpan,
  NumberLineSpan,
  StepsCardsContainer,
  StepCard,
  ImageIconWrapper,
} from './styles';

import { desktopBreakpoint, mobileBreakpoint } from '@/src/domains/shared/breakpoints';

export const TitleComponent = styled.div`
  ${({ theme }) => theme.typography.scale3}
  ${fluidProp(['font-size'], '32px', '42px')}
  color: var(--colors-blank);
  text-align: center;
  max-width: 650px;
  margin: 0 auto;
  ${fluidProp(['margin-bottom'], '46px', '90px')}

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }
`;

export const ProgressBar = styled.ul`
  list-style: none;
  counter-reset: step;
  padding-left: 0;

  ${desktopBreakpoint(css`
    display: flex;
    flex-direction: column;
    max-height: 50%;
    padding-top: 40px;

    // to remove progress bar in integrations, temporary solution, will be removed
    ${({ $align }) => {
      switch ($align) {
        case 'left':
          return css`
            display: none;
          `;
        default:
          return css`
            display: flex;
          `;
      }
    }}
  `)}

  ${mobileBreakpoint(css`
    padding-top: 70px;
  `)}
`;

export const StepTitle = styled.div`
  ${({ theme }) => theme.typography.titleSM}
  ${fluidProp(['font-size'], '18px', '24px')}
  color: var(--colors-blank);
  text-align: center;
  margin-bottom: 16px;

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }
`;

export const Label = styled.p`
  ${({ theme }) => theme.typography.bodyCaption}
  font-weight: 600;
  color: var(--colors-turquoise);
  text-transform: uppercase;
  margin-bottom: 16px;
`;

export const StepDescription = styled.div`
  ${({ theme }) => theme.typography.body}
  color: var(--colors-moonRaker);
  text-align: center;

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }

  ul {
    padding-left: 0;
    list-style: none;
  }

  li {
    position: relative;
    padding-left: 16px;

    &::before {
      position: absolute;
      top: 0;
      left: 0;
      content: '•';
      color: var(--colors-turquoise);
    }
  }
  // temporary solution, will be removed once new slider component is ready
  ${({ $align }) => {
    switch ($align) {
      case 'left':
        return css`
          text-align: left;
        `;
      default:
        return css`
          text-align: center;
        `;
    }
  }}
`;

export const StepsProgressBar = ({
  label,
  steps,
  element,
  title,
  align,
  customTitle,
  ...props
}) => {
  const sectionRef = useRef(null);

  const [activeState, setActiveState] = useState(
    Array(steps.length)
      .fill(false)
      .map((val, i) => (i ? val : true))
  );

  useEffect(() => {
    [...Array(steps.length - 1).keys()].reduce(
      (acc, current) =>
        acc.then(
          () =>
            new Promise((done) => {
              const ratioReference = 0.3; // with 0.5 it doesn't work on small screens (like iPhone 5 or etc)
              const intersectionObserver = new IntersectionObserver(
                (entries) => {
                  if (entries[0].intersectionRatio < ratioReference) return;
                  setTimeout(() => {
                    setActiveState((prevActiveState) => {
                      const updatedActiveState = [...prevActiveState];
                      updatedActiveState[current + 1] = true;
                      return updatedActiveState;
                    });
                    done();
                  }, 2000);
                },
                {
                  threshold: ratioReference,
                }
              );
              intersectionObserver.observe(sectionRef?.current);
            })
        ),
      Promise.resolve()
    );
  }, [steps.length]);

  return (
    <ProgressBarSectionWrapper {...props} ref={sectionRef}>
      <ProgressBarSectionHeader>
        {label && <Label>{label}</Label>}
        {customTitle ? (
          <TitleComponent>
            <h2>{customTitle}</h2>
          </TitleComponent>
        ) : (
          <RichText render={title} Component={TitleComponent} />
        )}
      </ProgressBarSectionHeader>
      <ProgressBarContainer>
        <ProgressBar $align={align}>
          {[...Array(steps.length).keys()].map((_, index) =>
            activeState[index + 1] ? (
              <ActiveNumberLineSpan key={index} />
            ) : (
              <NumberLineSpan key={index} />
            )
          )}
        </ProgressBar>
      </ProgressBarContainer>

      <StepsCardsContainer>
        {steps.map(
          ({ illustration, step_title: stepTitle, step_description: stepDescription }, index) => (
            <StepCard $active={activeState[index]} key={index}>
              <ImageIconWrapper>
                <Image
                  src={illustration.url}
                  alt={illustration.alt || ''}
                  width={illustration.dimensions.width}
                  height={illustration.dimensions.height}
                  quality="100"
                />
              </ImageIconWrapper>
              <RichText render={stepTitle} Component={StepTitle} />
              <RichText render={stepDescription} Component={StepDescription} $align={align} />
            </StepCard>
          )
        )}
      </StepsCardsContainer>
    </ProgressBarSectionWrapper>
  );
};
